import * as React from 'react';

import {
  AsyncBoundary,
  cn,
  ConsoleDialog,
  ConsoleTouchDialog,
  Row,
  TouchButton,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { EditModeGroupView } from '../../_shared/InventoryItemsModal/EditModeGroupView';
import { EditModeRootView } from '../../_shared/InventoryItemsModal/EditModeRootView';
import { MaterialListHeader } from '../../_shared/InventoryItemsModal/InventoryItemsModalHeader';
import { orderEntitiesByIds } from '../../_shared/InventoryItemsModal/orderedIds';

import { inventoryItemsQueryAtom } from '~/modules/trade/_graphql/queryInventoryItems';
import {
  $saveTouchModalEdits,
  $touchModalEditMode,
  $touchModalGroupOrderIds,
  $touchModalHasChanges,
  $touchModalOpen,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';
import { FALLBACK_MODAL_STYLE, useTheme } from '~/shared/hooks';

const $editModalItems = inventoryItemsQueryAtom((_get) => ({
  variables: { limit: 500 },
}));

const EMPTY_ARRAY: never[] = [];

//
// ⚛️ ⚛️ ⚛️ TOUCH MODE EDIT MODAL ⚛️ ⚛️ ⚛️
//
export const TouchModeEditModal = () => {
  const [isOpen, setIsOpen] = useAtom($touchModalOpen);

  if (!isOpen) return null;

  return (
    <TouchModeEditModalContent
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="@TouchModeEditModal"
    />
  );
};

type TouchModeEditModalContentProps = {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  className?: string;
};

const TouchModeEditModalContent = ({
  isOpen,
  setIsOpen,
  className,
}: TouchModeEditModalContentProps) => {
  const t = useTranslations();
  const { effectiveColorScheme } = useTheme();

  const {
    data: itemsData,
    isFetching: itemsLoading,
    error: itemsError,
  } = useAtomValue($editModalItems);
  const touchModalGroupOrderIds = useAtomValue($touchModalGroupOrderIds);
  const [editMode, setEditMode] = useAtom($touchModalEditMode);
  const hasChanges = useAtomValue($touchModalHasChanges);
  const saveEdits = useSetAtom($saveTouchModalEdits);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(
    null,
  );
  const [pendingClose, setPendingClose] = React.useState(false);
  const searchInputRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedGroupId(null);
      setSearchTerm('');
    }
  }, [isOpen]);

  const inventoryItems = itemsData?.allItems ?? EMPTY_ARRAY;

  const itemGroups = React.useMemo(() => {
    if (!itemsData?.itemGroups) return EMPTY_ARRAY;

    const allGroups: InventoryItemRecord[] = itemsData.itemGroups.map(
      (group) => ({
        id: group?.id ?? '',
        name: group?.name ?? '',
        itemGroupId: group?.id ?? '',
        itemGroup: {
          id: group?.id ?? '',
          name: group?.name ?? '',
          colorHue: group?.colorHue ?? null,
        },
        colorHue: group?.colorHue ?? null,
        metadata: { itemGroup: { colorHue: group?.colorHue ?? null } },
      }),
    );

    if (touchModalGroupOrderIds.length > 0) {
      return orderEntitiesByIds(allGroups, touchModalGroupOrderIds);
    }

    return allGroups;
  }, [itemsData?.itemGroups, touchModalGroupOrderIds]);

  const selectedItemGroup = itemGroups.find(
    (group) => group.id === selectedGroupId,
  );

  const itemsForSelectedGroup = React.useMemo(() => {
    if (!selectedGroupId) return [];
    return inventoryItems.filter(
      (item) => item.itemGroupId === selectedGroupId,
    );
  }, [inventoryItems, selectedGroupId]);

  const groupColorHue = React.useMemo(() => {
    if (!selectedGroupId) return undefined;
    return (
      itemGroups.find((group) => group.id === selectedGroupId)?.itemGroup
        ?.colorHue ?? undefined
    );
  }, [itemGroups, selectedGroupId]);

  const handleNavigateUp = () => {
    if (selectedGroupId) {
      setSelectedGroupId(null);
    } else {
      handleClose();
    }
  };

  const handleSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    setSearchTerm('');
  };

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && editMode && hasChanges) {
        setPendingClose(true);
        return;
      }
      if (!nextOpen) {
        setEditMode(false);
        setIsOpen(false);
      }
    },
    [editMode, hasChanges, setEditMode, setIsOpen],
  );

  const handleSave = React.useCallback(() => {
    saveEdits({ errorMessage: t.scaleTicketPro.settings.saveError });
  }, [saveEdits, t]);

  const handleClose = React.useCallback(() => {
    if (hasChanges) {
      setPendingClose(true);
      return;
    }
    setEditMode(false);
    setIsOpen(false);
  }, [hasChanges, setEditMode, setIsOpen]);

  const handleConfirmClose = React.useCallback(() => {
    setPendingClose(false);
    setEditMode(false);
    setIsOpen(false);
  }, [setEditMode, setIsOpen]);

  return (
    <>
      <ConsoleTouchDialog
        touchModeEnabled={true}
        effectiveColorScheme={effectiveColorScheme}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        className={cn('@TouchModeEditModal', className)}
        dialogClassName="h-full w-full max-w-full"
        classNameHeader="pb-0"
        contentStyle={FALLBACK_MODAL_STYLE}
        header={
          <MaterialListHeader
            searchInputRef={searchInputRef}
            selectedMaterialGroupId={selectedGroupId}
            onNavigateUp={handleNavigateUp}
            searchTerm={searchTerm}
            onChangeInput={setSearchTerm}
            clearSearchTerm={() => setSearchTerm('')}
            onSelectMaterialGroup={handleSelectGroup}
            itemGroups={itemGroups}
            autoFocus={isOpen && !selectedGroupId}
            mode="items"
          />
        }
        footer={
          <Row gap3 yCenter className="justify-end px-4 [&>*]:min-w-22">
            <TouchButton variant="outline" onPress={handleClose}>
              {t.common.cancel}
            </TouchButton>
            <TouchButton variant="solid" color="primary" onPress={handleSave}>
              <Row gap1 yCenter>
                <LucideCheck size={14} />
                {t.common.save}
              </Row>
            </TouchButton>
          </Row>
        }
      >
        <AsyncBoundary loading={!itemsData && itemsLoading} error={itemsError}>
          {!selectedGroupId ? (
            <EditModeRootView
              itemGroups={itemGroups}
              searchTerm={searchTerm}
              onSelectGroup={handleSelectGroup}
            />
          ) : (
            <EditModeGroupView
              selectedGroupId={selectedGroupId}
              selectedGroupName={selectedItemGroup?.name ?? ''}
              itemsForGroup={itemsForSelectedGroup}
              groupColorHue={groupColorHue}
              searchTerm={searchTerm}
            />
          )}
        </AsyncBoundary>
      </ConsoleTouchDialog>
      <ConsoleDialog
        isOpen={pendingClose}
        onOpenChange={(open) => {
          if (!open) setPendingClose(false);
        }}
        title={t.scaleTicketPro.settings.unsavedChangesTitle}
        content={
          <p className="text-sm">
            {t.scaleTicketPro.settings.unsavedChangesConfirm}
          </p>
        }
        footer={
          <Row gap2 className="justify-end">
            <TouchButton
              variant="outline"
              color="neutral"
              onPress={() => setPendingClose(false)}
            >
              {t.common.cancel}
            </TouchButton>
            <TouchButton color="failure" onPress={handleConfirmClose}>
              {t.common.discard}
            </TouchButton>
          </Row>
        }
      />
    </>
  );
};
